import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "sp-theme"; // 'light' | 'dark' | 'system'

function getSystemPref() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(mode) {
  const resolved = mode === "system" ? getSystemPref() : mode;
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  root.style.colorScheme = resolved;
}

/** React hook that wires dark-mode toggle with localStorage + system preference auto-detect. */
export function useTheme() {
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "system";
    return localStorage.getItem(STORAGE_KEY) || "system";
  });

  // Apply on mount + whenever mode changes.
  useEffect(() => {
    applyTheme(mode);
    if (mode === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const onChange = () => applyTheme("system");
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
  }, [mode]);

  const update = useCallback((next) => {
    if (next === "system") localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
    setMode(next);
  }, []);

  const resolved = mode === "system" ? getSystemPref() : mode;
  const toggle = useCallback(() => update(resolved === "dark" ? "light" : "dark"), [resolved, update]);

  return { mode, resolved, setMode: update, toggle };
}

/** Initialize theme as early as possible (called from index.js before React renders). */
export function bootstrapTheme() {
  if (typeof window === "undefined") return;
  const stored = localStorage.getItem(STORAGE_KEY);
  applyTheme(stored || "system");
}
